import { useCallback, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import type { Role } from '@lms/shared-types'
import { useNavigate } from '@tanstack/react-router'
import { useMoment } from '../../lib/moment-context'
import type { Density } from '../../lib/moment'
import { flags } from '../../lib/feature-flags'
import { roleLabel } from '../../lib/role'
import { Avatar } from '../primitives/Avatar'
import { Icon } from '../primitives/Icon'

/**
 * Profile dropdown menu for the TopBar.
 *
 * Trigger: avatar + name + chevron (desktop) / avatar only (tablet).
 * Panel: identity header, display density toggle, sign-out.
 *
 * Accessibility:
 *  - aria-haspopup="menu" on trigger
 *  - role="menu" on panel
 *  - role="menuitemradio" on density rows (with aria-checked)
 *  - role="menuitem" on sign-out
 *  - ↑↓ arrow key navigation between menu items
 *  - Escape closes + returns focus to trigger
 *  - Tab from trigger opens panel and moves to first item
 *  - Click outside closes
 */

export interface ProfileMenuProps {
  userName: string
  userEmail: string
  /** Full role set for this user. Used as the identity badge only when a
   *  team name isn't available (fallback), so the badge is never empty. */
  roles: Role[]
  /** The user's team name — shown as the identity badge in place of the role.
   *  Sourced from GET /api/v1/me (`team.name`). Falls back to the role
   *  label(s) when absent (e.g. team row missing, or in tests/stories). */
  teamName?: string | null | undefined
  onSignOut: () => void
  /** When true, collapse trigger to avatar-only (no name or chevron). */
  compact?: boolean
  /** When the menu sits on the dark gradient top-bar, flip the trigger's
   *  name/role/chevron to white so they read. The dropdown panel is
   *  unaffected (it renders on its own light surface). */
  onDark?: boolean
  /** Whether the user has a local ("credential") account. SSO (Entra) users
   *  have none, so the Account / change-password item is hidden for them. Only
   *  an explicit `false` hides it; undefined keeps today's behaviour. */
  hasLocalPassword?: boolean | undefined
}

const DENSITY_OPTIONS: { value: Density; label: string }[] = [
  { value: 'comfortable', label: 'Comfortable' },
  { value: 'compact', label: 'Compact' },
]

export function ProfileMenu({
  userName,
  userEmail,
  roles,
  teamName,
  onSignOut,
  compact = false,
  onDark = false,
  hasLocalPassword,
}: Readonly<ProfileMenuProps>): JSX.Element {
  const { density, setDensity } = useMoment()
  const [open, setOpen] = useState(false)

  // Self-defend against an empty role set so a misbehaving caller can never
  // render zero badges. Callers already fall back to [realRole]; this is a
  // belt-and-braces guard inside the component itself.
  const displayRoles: Role[] = roles.length > 0 ? roles : (['employee'] as Role[])

  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  // ── close on outside click ───────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent): void {
      if (
        panelRef.current &&
        triggerRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // ── keyboard: Escape closes and returns focus ────────────────────────────────
  const handlePanelKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
        return
      }
      // ↑↓ arrow key navigation among focusable menu items
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const items = Array.from(
          panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"],[role="menuitemradio"]') ?? [],
        )
        const idx = items.findIndex((el) => el === document.activeElement)
        const next = e.key === 'ArrowDown'
          ? (idx + 1) % items.length
          : (idx - 1 + items.length) % items.length
        items[next]?.focus()
      }
    },
    [],
  )

  // ── when panel opens, focus first item ──────────────────────────────────────
  useEffect(() => {
    if (!open) return
    // defer one tick so the panel is mounted
    const id = setTimeout(() => {
      const first = panelRef.current?.querySelector<HTMLElement>(
        '[role="menuitem"],[role="menuitemradio"]',
      )
      first?.focus()
    }, 0)
    return () => clearTimeout(id)
  }, [open])

  const handleTriggerKeyDown = useCallback((e: KeyboardEvent<HTMLButtonElement>): void => {
    if (e.key === 'Tab' && !open) {
      // Let Tab flow naturally; if panel is closed Tab moves away — no custom handling needed.
    }
  }, [open])

  const toggle = useCallback((): void => setOpen((prev) => !prev), [])

  const navigate = useNavigate()
  // SSO (Microsoft Entra) users have no local password to change — they manage it
  // in Microsoft — so hide the Account surface for them. Only an explicit `false`
  // hides it; missing (older BE / fixtures) keeps today's behaviour.
  const canManagePassword = hasLocalPassword !== false

  const handleAccount = useCallback((): void => {
    setOpen(false)
    void navigate({ to: '/me/account' })
  }, [navigate])

  const handleSignOut = useCallback((): void => {
    setOpen(false)
    onSignOut()
  }, [onSignOut])

  const handleDensity = useCallback(
    (value: Density): void => {
      setDensity(value)
    },
    [setDensity],
  )

  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      {/* ── Trigger ─────────────────────────────────────────────────────────── */}
      <button
        ref={triggerRef}
        type="button"
        data-testid="profile-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={compact ? `Profile menu for ${userName}` : undefined}
        onClick={toggle}
        onKeyDown={handleTriggerKeyDown}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: compact ? 0 : 8,
          minHeight: 44,
          minWidth: 44,
          padding: compact ? '0 4px' : '0 8px',
          background: 'transparent',
          border: '1px solid transparent',
          borderRadius: 8,
          cursor: 'pointer',
          color: onDark ? 'var(--surface)' : 'var(--text)',
          fontFamily: 'var(--font-sans)',
          transition: 'background var(--motion-duration-base) var(--motion-ease-out)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = onDark
            ? 'rgba(255, 255, 255, 0.14)'
            : 'var(--surface-2)'
          e.currentTarget.style.borderColor = onDark
            ? 'rgba(255, 255, 255, 0.24)'
            : 'var(--border)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent'
          e.currentTarget.style.borderColor = 'transparent'
        }}
      >
        <Avatar name={userName} size={32} />
        {!compact && (
          <>
            <span
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                minWidth: 0,
                maxWidth: 160,
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: onDark ? 'var(--surface)' : 'var(--text)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                }}
              >
                {userName}
              </span>
            </span>
            <Icon
              name="expand_more"
              size={16}
              aria-hidden
              style={{
                color: onDark ? 'rgba(255, 255, 255, 0.7)' : 'var(--text-3)',
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform var(--motion-duration-base) var(--motion-ease-out)',
              }}
            />
          </>
        )}
      </button>

      {/* ── Dropdown panel ──────────────────────────────────────────────────── */}
      {open && (
        <div
          ref={panelRef}
          role="menu"
          data-testid="profile-menu-panel"
          onKeyDown={handlePanelKeyDown}
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            width: compact ? 240 : 280,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            boxShadow: 'var(--shadow-2)',
            zIndex: 100,
            overflow: 'hidden',
            animation: 'lms-profile-menu-in var(--motion-duration-base) var(--motion-ease-out)',
          }}
        >
          <style>{`
            @keyframes lms-profile-menu-in {
              from { opacity: 0; transform: translateY(-4px); }
              to   { opacity: 1; transform: translateY(0); }
            }
            @media (prefers-reduced-motion: reduce) {
              @keyframes lms-profile-menu-in {
                from { opacity: 0; }
                to   { opacity: 1; }
              }
            }
          `}</style>

          {/* ── Identity section ─────────────────────────────────────────── */}
          <div
            data-testid="profile-menu-identity"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '16px 16px 14px',
              width: '100%',
              background: 'transparent',
              borderBottom: '1px solid var(--border)',
              textAlign: 'left' as const,
            }}
          >
            <Avatar name={userName} size={40} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: 'var(--text)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontFamily: 'var(--font-sans)',
                }}
              >
                {userName}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--text-2)',
                  fontFamily: 'var(--font-mono)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  marginTop: 1,
                }}
              >
                {userEmail}
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 4,
                  flexWrap: 'wrap',
                  marginTop: 4,
                }}
              >
                {teamName ? (
                  // Team name reads as a proper noun — sentence case, sans, not
                  // the uppercase-mono role-code styling.
                  <span
                    data-testid="profile-menu-team"
                    style={{
                      display: 'inline-block',
                      padding: '1px 6px',
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 4,
                      fontSize: 11,
                      fontFamily: 'var(--font-sans)',
                      color: 'var(--text-2)',
                    }}
                  >
                    {teamName}
                  </span>
                ) : (
                  // Fallback when the team name isn't available (missing team
                  // row, tests/stories) — keep the role badge so it's never empty.
                  displayRoles.map((r) => (
                    <span
                      key={r}
                      style={{
                        display: 'inline-block',
                        padding: '1px 6px',
                        background: 'var(--surface-2)',
                        border: '1px solid var(--border)',
                        borderRadius: 4,
                        fontSize: 10,
                        fontFamily: 'var(--font-mono)',
                        color: 'var(--text-2)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                      }}
                    >
                      {roleLabel(r)}
                    </span>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* ── Display section ──────────────────────────────────────────── */}
          {/* <div style={{ padding: '10px 0 6px' }}>
            <div
              style={{
                padding: '0 16px 6px',
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                color: 'var(--text-3)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              Display
            </div>

            {DENSITY_OPTIONS.map(({ value, label }) => (
              <DensityRow
                key={value}
                value={value}
                label={label}
                checked={density === value}
                onSelect={handleDensity}
              />
            ))}
          </div> */}

          {/* ── Account section (password mgmt) ──────────────────────────── */}
          {flags.passwordReset && canManagePassword ? (
            <div style={{ borderTop: '1px solid var(--border)', padding: '6px 0' }}>
              <MenuItemButton
                icon="key"
                label="Account"
                onClick={handleAccount}
                data-testid="profile-menu-account"
              />
            </div>
          ) : null}

          {/* ── Sign out ─────────────────────────────────────────────────── */}
          <div style={{ borderTop: '1px solid var(--border)', padding: '6px 0' }}>
            <MenuItemButton
              icon="logout"
              label="Sign out"
              destructive
              onClick={handleSignOut}
              data-testid="profile-menu-sign-out"
            />
          </div>
        </div>
      )}
    </div>
  )
}

// ── Sub-components ─────────────────────────────────────────────────────────────

interface DensityRowProps {
  value: Density
  label: string
  checked: boolean
  onSelect: (value: Density) => void
}

function DensityRow({ value, label, checked, onSelect }: Readonly<DensityRowProps>): JSX.Element {
  const [hover, setHover] = useState(false)

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      onSelect(value)
    }
  }, [value, onSelect])

  return (
    <div
      role="menuitemradio"
      aria-checked={checked}
      tabIndex={0}
      data-testid={`density-option-${value}`}
      onClick={() => onSelect(value)}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '7px 16px',
        cursor: 'pointer',
        background: hover ? 'var(--surface-2)' : 'transparent',
        transition: 'background var(--motion-duration-fast) var(--motion-ease-out)',
        outline: 'none',
      }}
    >
      {/* Inline radio indicator */}
      <span
        aria-hidden
        style={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          border: `2px solid ${checked ? 'var(--steel-blue)' : 'var(--line-strong)'}`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {checked && (
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: 'var(--steel-blue)',
            }}
          />
        )}
      </span>
      <span
        style={{
          fontSize: 13,
          fontFamily: 'var(--font-sans)',
          color: 'var(--text)',
        }}
      >
        {label}
      </span>
    </div>
  )
}

interface MenuItemButtonProps {
  icon: string
  label: string
  onClick: () => void
  destructive?: boolean
  'data-testid'?: string
}

function MenuItemButton({
  icon,
  label,
  onClick,
  destructive = false,
  'data-testid': testId,
}: Readonly<MenuItemButtonProps>): JSX.Element {
  const [hover, setHover] = useState(false)

  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '7px 16px',
        background: hover
          ? destructive
            ? 'color-mix(in srgb, var(--danger) 8%, transparent)'
            : 'var(--surface-2)'
          : 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: destructive ? 'var(--danger)' : 'var(--text)',
        fontFamily: 'var(--font-sans)',
        fontSize: 13,
        textAlign: 'left',
        transition: 'background var(--motion-duration-fast) var(--motion-ease-out)',
        outline: 'none',
        borderRadius: 0,
      }}
    >
      <Icon name={icon as Parameters<typeof Icon>[0]['name']} size={16} aria-hidden />
      {label}
    </button>
  )
}
